import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { PAYMENT_CURRENCY_OPTIONS } from "@/constants/program.constant";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClientApi } from "@/api/client.api";
import countries from "@/constants/array/country_city.json";
import { LoaderButton } from "@/components/ui/loader-button";
import {
  clientContactNumberSchema,
  sanitizeClientContactNumber,
} from "@/utils/client-contact.utils";

const CURRENCY_OPTIONS = [
  { id: 1, label: "United State Dollar", value: "USD" },
  { id: 2, label: "EURO", value: "EUR" },
  { id: 3, label: "British Pound", value: "GBP" },
  { id: 4, label: "Indian Rupee", value: "INR" },
  { id: 5, label: "United Arab Emirates Dirham", value: "AED" },
  { id: 6, label: "Ethiopian Birr", value: "ETB" },
];

const ClientSchema = Yup.object().shape({
  clientCode: Yup.string().required("Client Code is required"),
  clientName: Yup.string().required("Client Name is required"),
  clientContactNumber: clientContactNumberSchema,
  clientEmail: Yup.string()
    .email("Invalid email address")
    .required("Client Email is required"),
  country: Yup.string().required("Country is required"),
  currency: Yup.string().required("Currency is required"),
  address: Yup.string().required("Address is required"),
});

const CreateClient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: yupResolver(ClientSchema),
    defaultValues: {
      clientCode: "",
      clientName: "",
      clientContactNumber: "",
      clientEmail: "",
      country: "",
      currency: "USD",
      address: "",
    },
  });

  const onSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);
      // Map form data to API payload
      const currencyId =
        CURRENCY_OPTIONS.find(
          (currency) => currency.value === formData.currency
        )?.id || 1;

      const payload = {
        clientCode: formData.clientCode,
        clientName: formData.clientName,
        clientAddress: formData.address,
        clientContactNumber: formData.clientContactNumber,
        clientEmail: formData.clientEmail,
        clientCountry: formData.country,
        currencyId: currencyId,
      };

      await ClientApi.createClient(payload);
      toast.success("Client created successfully");
      navigate(getClientListPath());
    } catch (error) {
      toast.error(error?.message || "Failed to create client");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getClientListPath = () => {
    if (location.pathname.startsWith("/xpandpoint"))
      return "/xpandpoint/clients";
    if (location.pathname.startsWith("/rewardpoint"))
      return "/rewardpoint/clients";
    if (location.pathname.startsWith("/accesspoint"))
      return "/accesspoint/clients";
    return "/clients";
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to={getClientListPath()}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Create New Client
          </h1>
          <p className="text-gray-500">Add a new client to your platform</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 gap-8">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <FormField
                  control={form.control}
                  name="clientCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Code*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. ADCB" {...field} />
                      </FormControl>
                      <FormDescription>
                        Unique identifier for the client
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clientName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Name*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Abu Dhabi Commercial Bank"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Full name of the client organization
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clientContactNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Contact Number*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. 501234567"
                          inputMode="numeric"
                          maxLength={15}
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              sanitizeClientContactNumber(e.target.value)
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Primary contact number for the client
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clientEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Email*</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. client@email.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        Email address for the client
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(countries as any)?.map((country) => (
                            <SelectItem
                              key={country?.name}
                              value={country?.name}
                            >
                              {country?.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Country where the client is located
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter full address" {...field} />
                      </FormControl>
                      <FormDescription>
                        Complete address of the client
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((currency) => (
                            <SelectItem
                              key={currency.value}
                              value={currency.value}
                            >
                              {currency.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Primary currency for the client
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/accesspoint/clients")}
            >
              Cancel
            </Button>
            <LoaderButton type="submit" loading={isSubmitting}>
              <Save className="h-4 w-4 mr-2" /> Create Client
            </LoaderButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CreateClient;

<style>{`.loader { border: 2px solid #f3f3f3; border-top: 2px solid #3498db; border-radius: 50%; width: 16px; height: 16px; animation: spin 1s linear infinite; display: inline-block; vertical-align: middle; } @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>;
